import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  HelpCircle,
  MapPin,
  Truck,
  type LucideIcon,
} from 'lucide-react'

import { de } from '../i18n/de'
import type { ParcelStatus } from '../api/types'

interface StatusMeta {
  label: string
  icon: LucideIcon
  /** Icon and label above the route dots. Only what asks for action gets coloured text. */
  text: string
  /** Timeline dots in the drawer's history. */
  dot: string
}

/*
 * Colour only where it means something: sky means "go and collect it", red a problem,
 * green the day it arrives. Everything else reads in the normal text colour, delivered and
 * unknown a step quieter. Orange is left to the app's own accent.
 */
const QUIET_TEXT = 'text-slate-700 dark:text-slate-300'

export const STATUS_META: Record<ParcelStatus, StatusMeta> = {
  EXCEPTION: {
    label: de.status.EXCEPTION,
    icon: AlertTriangle,
    text: 'font-semibold text-red-700 dark:text-red-400',
    dot: 'bg-red-500',
  },
  OUT_FOR_DELIVERY: {
    label: de.status.OUT_FOR_DELIVERY,
    icon: Truck,
    text: 'font-semibold text-emerald-700 dark:text-emerald-400',
    dot: 'bg-emerald-600',
  },
  PICKUP_READY: {
    label: de.status.PICKUP_READY,
    icon: MapPin,
    text: 'font-semibold text-sky-700 dark:text-sky-400',
    dot: 'bg-sky-500',
  },
  IN_TRANSIT: {
    label: de.status.IN_TRANSIT,
    icon: Truck,
    text: QUIET_TEXT,
    dot: 'bg-emerald-400',
  },
  ANNOUNCED: {
    label: de.status.ANNOUNCED,
    icon: Clock,
    text: QUIET_TEXT,
    dot: 'bg-slate-400',
  },
  DELIVERED: {
    label: de.status.DELIVERED,
    icon: CheckCircle2,
    text: 'text-slate-500 dark:text-slate-400',
    dot: 'bg-emerald-600',
  },
  UNKNOWN: {
    label: de.status.UNKNOWN,
    icon: HelpCircle,
    text: 'text-slate-500 dark:text-slate-400',
    dot: 'bg-slate-400',
  },
}

/**
 * The route every parcel takes, one dot per step. Waiting at a pickup point and a problem
 * are not steps of their own: they take the place of the step the parcel is stuck at.
 */
export const ROUTE_STEPS = ['ANNOUNCED', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED'] as const

export type RouteTone = 'today' | 'pickup' | 'problem'

export interface RoutePosition {
  /** How many steps, from the start, are done. */
  done: number
  /** The step the parcel stands at right now when that is news, drawn larger and coloured. */
  current: { step: number; tone: RouteTone } | null
}

const STEP_OF: Partial<Record<ParcelStatus, number>> = {
  ANNOUNCED: 0,
  IN_TRANSIT: 1,
  OUT_FOR_DELIVERY: 2,
  PICKUP_READY: 2,
  DELIVERED: 3,
}

/**
 * Where on the route a status puts the parcel.
 *
 * A problem has no place of its own, so it sits right after the last step the parcel is
 * known to have reached. The list does not know that step and passes nothing; the drawer
 * has the history and passes the newest status before the problem. Without it the problem
 * is placed after "Unterwegs", where nearly all of them happen.
 */
export function routePosition(status: ParcelStatus, reached?: ParcelStatus): RoutePosition {
  switch (status) {
    case 'UNKNOWN':
      return { done: 0, current: null }
    case 'ANNOUNCED':
      return { done: 1, current: null }
    case 'IN_TRANSIT':
      return { done: 2, current: null }
    case 'OUT_FOR_DELIVERY':
      return { done: 2, current: { step: 2, tone: 'today' } }
    case 'PICKUP_READY':
      return { done: 2, current: { step: 2, tone: 'pickup' } }
    case 'DELIVERED':
      return { done: 4, current: null }
    case 'EXCEPTION': {
      const last = reached == null ? undefined : STEP_OF[reached]
      // Never past the last step: a problem after delivery still stands before it.
      const step = Math.min(last == null ? 2 : last + 1, ROUTE_STEPS.length - 1)
      return { done: step, current: { step, tone: 'problem' } }
    }
  }
}

/**
 * The newest status in a parcel's history that is a step of the route, for placing a
 * problem. Events come newest first.
 */
export function lastRouteStatus(events: { status: ParcelStatus }[]): ParcelStatus | undefined {
  return events.find((event) => STEP_OF[event.status] != null)?.status
}

/**
 * A small swatch per carrier, in roughly its own colour: enough to recognise DHL or UPS
 * at a glance without a row of loud coloured labels.
 */
export const CARRIER_SWATCHES: Record<string, string> = {
  DHL: 'bg-yellow-400',
  // Distinct from DHL Paket on purpose: different services with different delivery
  // promises, and confusing them on the board would be misleading.
  DHL_EXPRESS: 'bg-red-600',
  DPD: 'bg-rose-700',
  GLS: 'bg-blue-700',
  HERMES: 'bg-sky-500',
  UPS: 'bg-amber-800',
  FEDEX: 'bg-purple-700',
  AMAZON: 'bg-slate-900 dark:bg-slate-200',
  DEUTSCHE_POST: 'bg-yellow-300',
  OTHER: 'bg-slate-400',
}

export function carrierSwatch(code: string): string {
  return CARRIER_SWATCHES[code] ?? CARRIER_SWATCHES.OTHER
}
